import { useTranslation } from 'react-i18next';

interface Props {
  bmi: number | null;
}

interface Band {
  label: string;
  min: number;
  max: number;
  color: string;
}

// WHO adult BMI classification
const BANDS: Band[] = [
  { label: 'underweight', min: 0, max: 18.5, color: '#0ea5e9' },
  { label: 'normal', min: 18.5, max: 25, color: '#10b981' },
  { label: 'overweight', min: 25, max: 30, color: '#f59e0b' },
  { label: 'obese', min: 30, max: 45, color: '#ef4444' },
];

const DISPLAY_MIN = 12;
const DISPLAY_MAX = 40;

function classifyBmi(bmi: number): keyof typeof BAND_KEYS {
  if (bmi < 18.5) return 'underweight';
  if (bmi < 25) return 'normal';
  if (bmi < 30) return 'overweight';
  return 'obese';
}

const BAND_KEYS = { underweight: 1, normal: 1, overweight: 1, obese: 1 };

export function BmiGauge({ bmi }: Props) {
  const { t } = useTranslation();

  if (bmi == null || Number.isNaN(bmi)) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-slate-500">
        No BMI data yet.
      </div>
    );
  }

  const clamped = Math.min(DISPLAY_MAX, Math.max(DISPLAY_MIN, bmi));
  const pct = ((clamped - DISPLAY_MIN) / (DISPLAY_MAX - DISPLAY_MIN)) * 100;
  const classification = classifyBmi(bmi);
  const activeBand = BANDS.find((b) => b.label === classification)!;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-bold tabular-nums" style={{ color: activeBand.color }}>
          {bmi.toFixed(1)}
        </span>
        <span className="text-sm text-slate-500">{t('records.bmiCategory.' + activeBand.label)}</span>
      </div>

      {/* Bar */}
      <div className="relative h-3 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800">
        <div
          className="absolute inset-y-0 left-0 flex"
          style={{ width: '100%' }}
          aria-hidden
        >
          {BANDS.map((band) => {
            const left = ((band.min - DISPLAY_MIN) / (DISPLAY_MAX - DISPLAY_MIN)) * 100;
            const right = ((band.max - DISPLAY_MIN) / (DISPLAY_MAX - DISPLAY_MIN)) * 100;
            const width = Math.max(0, right - left);
            return (
              <div
                key={band.label}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  width: `${width}%`,
                  height: '100%',
                  backgroundColor: band.color,
                  opacity: band.label === classification ? 1 : 0.4,
                }}
              />
            );
          })}
        </div>
        <div
          className="absolute top-1/2 -translate-y-1/2 w-1 h-5 bg-slate-900 dark:bg-slate-100 rounded-full shadow-md"
          style={{ left: `calc(${pct}% - 2px)` }}
          aria-hidden
        />
      </div>

      <div className="flex justify-between text-[10px] text-slate-500 tabular-nums">
        <span>{DISPLAY_MIN}</span>
        <span>18.5</span>
        <span>25</span>
        <span>30</span>
        <span>{DISPLAY_MAX}</span>
      </div>
    </div>
  );
}
